import React, { useEffect, useState } from 'react';
import Table, { TableRow } from '../../common/Widgets/Table';
import TabsComponent from '../../common/Tabs/TabsComponent';
import {
  InsuranceJourneyContext,
  InsuranceJourneyInsuranceProductsType,
  PaymentMethod
} from '../../common/CommonTypes';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { formatNumber } from '../../common/Formatter';
import { PageAbsolutePath } from '../../../consts/Common';

type PaymentSummaryProps = {
  headerText: string;
  policyStartDate?: string;
  disableTabs: boolean;
  redirectOnMonthlySelected?: boolean;
  salesTelephoneNumber: string;
  message?: string;
  messageTitle?: string;
  hideMonthlyIfZero: boolean;
};

export const getAnnualTotalPayable = (
  insuranceProducts: InsuranceJourneyInsuranceProductsType
): number => {
  let total = insuranceProducts.annualPayment;

  insuranceProducts.optionalProductList.forEach((product) => {
    if (product.isOptionalProductSelected) {
      if (product.optionalSubProductList.length > 0) {
        const selectedSubProduct = product.optionalSubProductList.find(
          (sp) => sp.isOptionalSubProductSelected
        );
        if (selectedSubProduct) {
          total += selectedSubProduct.optionalSubProductPrice;
        }
      } else {
        total += product.optionalProductPrice;
      }
    }
  });

  if (insuranceProducts.arrangementFee !== 0) {
    total += insuranceProducts.arrangementFee;
  }

  return total;
};

const PaymentSummary = ({
  headerText,
  policyStartDate,
  disableTabs,
  redirectOnMonthlySelected,
  salesTelephoneNumber,
  message,
  messageTitle,
  hideMonthlyIfZero
}: PaymentSummaryProps): JSX.Element => {
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { insuranceProducts, paymentDetails, drivingHistoryDetails } = journey;
  const { commonEvents } = pageEvents;
  const { setSelectedPaymentMethod } = commonEvents;
  const navigate = useNavigate();
  const [monthlySelected, setMonthlySelected] = useState(false);

  useEffect(() => {
    if (
      monthlySelected &&
      paymentDetails.selectedPaymentMethod == PaymentMethod.monthly
    ) {
      navigate(
        PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice
      );
    }
  }, [paymentDetails.selectedPaymentMethod, monthlySelected]);

  const onTabClick = (tab: PaymentMethod) => {
    if (!disableTabs) {
      setSelectedPaymentMethod(tab);
      if (redirectOnMonthlySelected && tab === PaymentMethod.monthly) {
        setMonthlySelected(true);
      }
    }
  };

  const getFeeBreakdownRows = (): TableRow[] => {
    const feeBreakdownRows: TableRow[] = [];
    let total = 0.0;

    feeBreakdownRows.push({
      columns: [
        'Annual Motor Insurance (Underwritten by ABC Insurance):',
        drivingHistoryDetails.ncdDiscount
          ? `£${formatNumber(insuranceProducts.annualPayment, 2, 'en-GB')}**`
          : `£${formatNumber(insuranceProducts.annualPayment, 2, 'en-GB')}`
      ]
    });
    total += insuranceProducts.annualPayment;

    insuranceProducts.optionalProductList.forEach((product) => {
      if (product.isOptionalProductSelected) {
        if (product.optionalSubProductList.length > 0) {
          const selectedSubProduct = product.optionalSubProductList.find(
            (sp) => sp.isOptionalSubProductSelected
          );
          if (selectedSubProduct) {
            feeBreakdownRows.push({
              columns: [
                `${product.optionalProductName} (${selectedSubProduct.optionalSubProductName})`,
                `£${formatNumber(selectedSubProduct.optionalSubProductPrice, 2, 'en-GB')}`
              ]
            });
            total += selectedSubProduct.optionalSubProductPrice;
          }
        } else {
          feeBreakdownRows.push({
            columns: [
              product.optionalProductName,
              `£${formatNumber(product.optionalProductPrice, 2, 'en-GB')}`
            ]
          });
          total += product.optionalProductPrice;
        }
      }
    });

    if (insuranceProducts.arrangementFee !== 0) {
      feeBreakdownRows.push({
        columns: [
          'Insurance Broker Arrangement Fee:',
          `£${formatNumber(insuranceProducts.arrangementFee, 2, 'en-GB')}`
        ]
      });
      total += insuranceProducts.arrangementFee;
    }

    feeBreakdownRows.push({
      columns: [
        'Total cost of the cover selected',
        `£${formatNumber(total, 2, 'en-GB')}`
      ]
    });

    return feeBreakdownRows;
  };

  const haveChosenMonthlyRows: TableRow[] = [
    {
      columns: [
        'Deposit payment:',
        `£${formatNumber(insuranceProducts.monthlyDeposit, 2, 'en-GB')}`
      ]
    },
    {
      columns: [
        'First instalment of*:',
        `£${formatNumber(insuranceProducts.monthlyFirstInstallment, 2, 'en-GB')}`
      ]
    },
    {
      columns: [
        `${insuranceProducts.monthlyInstallmentNumber} Monthly instalments of:`,
        `£${formatNumber(insuranceProducts.monthlyInstallment, 2, 'en-GB')}`
      ]
    },
    {
      columns: [
        'APR:',
        `${formatNumber(insuranceProducts.monthlyAPR, 2, 'en-GB')}%`
      ]
    }
  ];

  const haveChosenAnnuallyRows: TableRow[] = [
    {
      columns: [
        'One off payment:',
        `£${formatNumber(getAnnualTotalPayable(insuranceProducts), 2, 'en-GB')}`
      ]
    }
  ];

  const iptMessage =
    'Note: Prices displayed are inclusive of IPT (Insurance Premium Tax) where applicable';

  const ncdMessage =
    drivingHistoryDetails.ncdDiscount || insuranceProducts.showNCDPrice
      ? 'and protected NCD at ' +
        `£${formatNumber(insuranceProducts.ncdPrice, 2, 'en-GB')}` +
        '.Please note that protection only applies to your NCD. Your price may still change even if you have an accident that’s not your fault.'
      : '';
  const protectedNCDMessage =
    drivingHistoryDetails.ncdDiscount || insuranceProducts.showNCDPrice
      ? '**includes Protected NCD at ' +
        `£${formatNumber(insuranceProducts.ncdPrice, 2, 'en-GB')}`
      : '';

  const roundingMessage =
    insuranceProducts.maxRoundingDifference > 0
      ? `Due to rounding of the monthly repayment amount your first repayment may differ by a maximum of ${formatNumber(insuranceProducts.maxRoundingDifference * 100, 0, 'en-GB')}p.`
      : '';

  const messageSection = () => {
    if (!messageTitle && !message) {
      return <></>;
    }

    return (
      <div>
        {messageTitle && <h4 className='zero-bottom-margin'>{messageTitle}</h4>}
        {message && <p>{message}</p>}
        <hr />
      </div>
    );
  };

  const payMonthly = () => {
    if (insuranceProducts.monthlyInstallment === 0) {
      return `Unfortunately, we are currently unable to offer monthly payments online. Please call us on ${salesTelephoneNumber} or try again later. Alternatively, you can pay annually by clicking the 'pay annually' tab`;
    }

    return (
      <div className='button-tab'>
        {messageSection()}
        <div className='flex-row'>
          <div className='two-columns'>
            <h4 className='section-header'>{`${headerText} £${formatNumber(insuranceProducts.monthlyInstallment, 2, 'en-GB')} a month`}</h4>
            <Table
              id='fee-breakdown-monthly-table'
              rows={getFeeBreakdownRows()}
            />
            {policyStartDate && <p>Start date: {policyStartDate}</p>}
            <p>{protectedNCDMessage}</p>
            <p>{iptMessage}</p>
          </div>
          <div className='two-columns'>
            <div className='have-chosen-box m1-bottom'>
              <h4 className='section-header m3-top'>
                How you've chosen to pay
              </h4>
              <Table id='chosen-monthly-table' rows={haveChosenMonthlyRows} />
              <p>
                {`*First instalment includes a £${formatNumber(insuranceProducts.monthlyFacilityFee, 2, 'en-GB')} facility fee. You will only pay the 'Total Payable' amount shown. ${roundingMessage}`}
              </p>
              <p>
                Please note: It will cost more if you pay monthly as interest is
                charged on the amount you borrow.
              </p>
              {insuranceProducts.arrangementFee !== 0 && (
                <p>{`Your price includes a £${formatNumber(insuranceProducts.arrangementFee, 2, 'en-GB')} arrangement fee ${ncdMessage}`}</p>
              )}
              <p>Insurance Premium Tax is included at the prevailing rate.</p>
            </div>
            <h4 className='section-header'>{`Total payable £${formatNumber(insuranceProducts.monthlyTotal, 2, 'en-GB')}`}</h4>
          </div>
        </div>
      </div>
    );
  };

  const payAnnually = () => {
    return (
      <div className='button-tab'>
        {messageSection()}
        <div className='flex-row'>
          <div className='two-columns'>
            <h4 className='section-header'>{`${headerText} £${formatNumber(getAnnualTotalPayable(insuranceProducts), 2, 'en-GB')}`}</h4>
            <Table
              id='fee-breakdown-Annually-table'
              rows={getFeeBreakdownRows()}
            />
            {policyStartDate && <p>Start date: {policyStartDate}</p>}
            <p>{iptMessage}</p>
            <p>{protectedNCDMessage}</p>
          </div>
          <div className='two-columns'>
            <div className='have-chosen-box m1-bottom'>
              <h4 className='section-header m3-top'>
                How you've chosen to pay
              </h4>
              <Table id='chosen-annual-table' rows={haveChosenAnnuallyRows} />
              {insuranceProducts.arrangementFee !== 0 && (
                <p>{`Your price includes a £${formatNumber(insuranceProducts.arrangementFee, 2, 'en-GB')} arrangement fee ${ncdMessage}`}</p>
              )}
              <p>Insurance Premium Tax is included at the prevailing rate.</p>
            </div>
            <h4 className='section-header'>{`Total payable £${formatNumber(getAnnualTotalPayable(insuranceProducts), 2, 'en-GB')}`}</h4>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='payment-summary'>
      <TabsComponent
        onClick={onTabClick}
        selectedTab={paymentDetails.selectedPaymentMethod}
        hideMonthly={
          hideMonthlyIfZero && insuranceProducts.monthlyInstallment === 0
        }
      />
      <div className='payment-section'>
        {paymentDetails.selectedPaymentMethod === PaymentMethod.annually &&
          payAnnually()}
        {paymentDetails.selectedPaymentMethod === PaymentMethod.monthly &&
          payMonthly()}
      </div>
    </div>
  );
};

export default PaymentSummary;
